/* global */

import {
  CoObjectItem,
  DocumentPartSource,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../../components/interfaces/CoObject";
import {
  CoObject,
  CoObjectDocumentPartInclude,
  CoObjectDropdown,
  CoObjectTypeEnum,
  DocumentPart,
  ElaborationItem,
  SourceEnum,
  TgkAmountElaborationRequest,
  TgkDimensionAttributeElaborationRequest,
  TgkTableElaborationRequest,
  TgkTextElaborationRequest,
} from "../../../api/models";
import { SourceTypeEnum } from "../../../api/models/source-type-enum";
import { store } from "../../store/store";
import { FormatTypeEnum } from "../../../api/models/format-type-enum";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import { normalizePath } from "../document/document-slice";
import * as uuid from "uuid";
import { ElaborationItemType, UpdateOptions } from "../../../api/object/RefreshObject";
import { TableUpdateStrategy } from "../../../components/coobjects/co-panel/ObjectPanelComponent";
import { DimensionDetail } from "../../../api/models/dimension-detail";
import { DimensionParameter } from "../../../api/models/dimension-parameter";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { ElabResultType } from "../../../api/models/elab-result-type";
import { TgkAmountAihElaborationRequest } from "../../../api/models/tgk-amount-aih-elaboration-request";
import { isComponentInstance } from "../../../utils/helpers/ComponentInstanceHelper";

export function mapCoObjectToTgkTableElaborationRequest(
  documentPart: DocumentPart,
  coObject: CoObjectItem,
  language: string,
  userId: string,
  tableUpdateType: TableUpdateStrategy["typeUpdate"]
): TgkTableElaborationRequest {
  const source = coObject.source as TgkTableObjectSource;
  const insertAsImage = source.elabResultType === "IMAGE_STRINGBASE64";
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    source: SourceEnum[source.objectSource],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    isComponent: coObject.info.isComponent,
    documentId: coObject.documentId,
    form: {
      dbId: source.objectSource !== SourceTypeEnum.EXCEL ? source.dbApplication.id : "",
      formId: source.form.code,
      formDesc: source.form.desc,
      path: source.form.oidPath ? source.form.oidPath.toString() : "",
    },
    sheetCode: source.sheet.code,
    sheet:
      source.objectSource !== SourceTypeEnum.EXCEL || coObject.info?.objectType === "CHART"
        ? source.sheet.desc
        : getSheetForExcelTableObject(source),
    area: source.area?.name,
    chart: source.chart?.name,
    contentControlId: coObject.contentControlId.toString(),
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    formatMetadata: coObject?.advanced?.formatMetadata,
    formatImage: insertAsImage ? source.formatImage : {},
    parameters: coObject.parameters as DimensionDetail[],
    inheritFormat: coObject.advanced.inheritFormat,
    inheritFormatImage: insertAsImage ? source.inheritFormatImage : null,
    language: language,
    elabResultType: source.elabResultType as ElabResultType,
    tableUpdateStrategy: {
      typeUpdate: tableUpdateType,
      overrideData: coObject.info.overrideData,
      overrideStyle: coObject.info.overrideStyle,
      originalPayload: coObject.originalPayload,
    },
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info.componentReference,
    }),
  };
}

export function mapCoObjectToTgkAmountElaborationRequest(
  coObject: CoObjectItem,
  language: string,
  userId: string
): TgkAmountElaborationRequest {
  const source = coObject.source as TgkAmountObjectSource;
  let formatType = coObject.advanced.choice;
  let elabResultType = source.elabResultType;
  let cell = null;
  let area = null;
  let sheet = null;
  let sheetCode = null;
  let formId = null;
  let formDesc = null;
  let dimensions = [];
  let parameters = null;
  if (source.objectSource === undefined || source.objectSource === "") {
    source.objectSource = SourceTypeEnum.TAGETIK;
  }
  let objectSource = source.objectSource;

  switch (source.objectSource) {
    case SourceTypeEnum.EXCEL:
      formatType = FormatTypeEnum.NUMBER;
      formId = source.form?.code;
      formDesc = source.form?.desc;
      sheetCode = source.sheet.code;
      sheet = source.cell === "" || source.cell == undefined ? getSheetForExcelAmountObject(source) : source.sheet.desc;
      area = source.area?.name;
      cell = source.cell;
      break;
    case SourceTypeEnum.TAGETIK_FORM:
      formatType = FormatTypeEnum.NUMBER;
      formId = source.form?.code;
      formDesc = source.form?.desc;
      sheetCode = source.sheet.code;
      sheet = source.sheet.desc;
      area = source.area?.name;
      cell = source.cell;
      parameters = coObject.parameters;
      break;
    default:
      dimensions = Object.values(source.dimensions);
  }

  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.source.type ?? coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info ?? "",
    isComponent: coObject.info.isComponent,
    dimensions: dimensions,
    dbId: source.dbApplication?.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId.toString(),
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    formatMetadata: coObject?.advanced?.formatMetadata,
    formatType: formatType as FormatTypeEnum,
    inheritFormat: coObject.advanced.inheritFormat,
    language: language,
    elabResultType: elabResultType as ElabResultType,
    source: objectSource as SourceEnum,
    formId: formId,
    formDesc: formDesc,
    sheetCode: sheetCode,
    sheet: sheet,
    area: area,
    cell: cell,
    ...(parameters && { parameters }),
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info.componentReference,
    }),
  };
}

export function mapCoObjectToTgkAmountAihElaborationRequest(
  coObject: CoObjectItem,
  userId: string
): TgkAmountAihElaborationRequest {
  const source = coObject.source as TgkAmountObjectSource;

  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.source.type],
    name: coObject.info.objectName,
    desc: coObject.info.info ?? "",
    isComponent: coObject.info.isComponent,
    dimensions: Object.values(source.dimensions),
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId.toString(),
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    formatMetadata: coObject?.advanced?.formatMetadata,
    formatType: coObject.advanced.choice as FormatTypeEnum,
    inheritFormat: coObject.advanced.inheritFormat,
    elabResultType: source.elabResultType as ElabResultType,
    analyticalWorkspace: source.analyticalWorkspace,
    dataset: source.dataset,
    amount: source.amount,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info.componentReference,
    }),
  };
}

export function mapCoObjectToTgkDimensionAttributeElaborationRequest(
  coObject: CoObjectItem,
  language: string,
  userId: string
): TgkDimensionAttributeElaborationRequest {
  const source = coObject.source as TgkDimensionAttributeSource;
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    isComponent: coObject.info.isComponent,
    dimensions: source.dimensions,
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId.toString(),
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    elabResultType: source.elabResultType as ElabResultType,
    language: language,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info.componentReference,
    }),
  };
}

export function mapCheckboxConfigToCheckbox(coObject: CoObjectItem): CoObject {
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    type: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
  };
}

export function mapDropDownConfigToDropDown(coObject: CoObjectItem): CoObjectDropdown {
  let source: TgkDropDownSource = coObject.source as TgkDropDownSource;
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    type: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    dropDownList: source.dropDownList,
  };
}

export function mapCoObjectToTgkTextElaborationRequest(
  coObject: CoObjectItem,
  userId: string
): TgkTextElaborationRequest {
  const source = coObject.source as TgkTextSource;
  return {
    id: coObject.id,
    objectType: CoObjectTypeEnum[coObject.info.objectType],
    name: coObject.info.objectName,
    desc: coObject.info.info ?? "",
    isComponent: coObject.info.isComponent,
    dimensions: source.dimensions ? source.dimensions : [],
    dbId: source.dbApplication.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId.toString(),
    clientId: store.getState().configurationState.clientId,
    correlationId: uuid.v4(),
    userId: userId,
    objectId: coObject.info.objectName,
    analyticalWorkspace: source.analyticalWorkspace,
    dataset: source.dataset,
    richText: source.richText,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info.componentReference,
    }),
  };
}

export function mapFreeTextConfigToFreeText(coObject: CoObjectItem): CoObject {
  return {
    id: coObject.id,
    isLocked: coObject.isLocked,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    name: coObject.info.objectName,
    desc: coObject.info.info,
    type: CoObjectTypeEnum[coObject.info.objectType],
  };
}

export function mapDocumentPartConfigToDocumentPart(
  coObject: CoObjectItem,
  inheritableParameters?: DimensionParameter[],
  language?: string
): CoObjectDocumentPartInclude {
  const source: DocumentPartSource = coObject.source as DocumentPartSource;
  return {
    documentPartMetadata: {
      documentId: null,
      formatMetadata: coObject?.advanced?.formatMetadata,
      formatImage: coObject?.advanced?.formatImage,
      parametersMetadata: coObject.parameters as DimensionParameter[],
      inheritableParametersMetadata: inheritableParameters,
      language: language,
      templateInfo: coObject?.advanced?.templateInfo,
    },
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    name: normalizeDocumentName(coObject.info.objectName),
    desc: coObject.info.info,
    type: CoObjectTypeEnum[coObject.info.objectType],
    path: normalizePath(source.path),
    includedDocumentPartId: source.includedDocumentPartId,
  };
}

export async function mapCoObjectToRefreshElaborationItem(
  correlationId: string,
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  coObject: CoObjectItem,
  selectedUpdateOption?: UpdateOptions,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"],
  fileExcelName?: string
): Promise<ElaborationItem> {
  const elaborationType: ElaborationItemType = isComponentInstance(coObject) ? "REFRESH_COMPONENT_INSTANCE" : "REFRESH";

  if (
    coObject.contentControlId === null ||
    coObject.contentControlId === undefined ||
    `${coObject.contentControlId}` === ""
  ) {
    throw new Error(
      `Object "${coObject.info?.objectName}" of type ${coObject.info.objectType} has no content control id`
    );
  }

  switch (coObject.info.objectType) {
    case "TABLE":
    case "CHART":
      return mapTgkTableOrChartToElaborationItem(
        correlationId,
        documentPart,
        documentPartMetadata,
        coObject,
        elaborationType,
        tableUpdateType,
        fileExcelName
      );
    case "AMOUNT":
      return mapTgkAmountToElaborationItem(
        correlationId,
        documentPart,
        documentPartMetadata,
        coObject,
        elaborationType,
        fileExcelName
      );
    case "DIMENSION_ATTRIBUTE":
      return mapDimensionAttributeToElaborationItem(
        correlationId,
        documentPart,
        documentPartMetadata,
        coObject,
        elaborationType
      );
    case "TGK_TEXT":
      return mapTgkTextToElaborationItem(correlationId, documentPart, documentPartMetadata, coObject, elaborationType);
    case "DOCUMENT_PART_INCLUDE":
      return mapDocumentPartIncludeToElaborationItem(
        correlationId,
        documentPart,
        coObject,
        "REFRESH",
        selectedUpdateOption,
        tableUpdateType
      );
    default:
      throw new Error(`Unsupported object type ${coObject.info.objectType}`);
  }
}

const mapTgkTableOrChartToElaborationItem = async (
  correlationId: string,
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  coObject: CoObjectItem,
  elaborationType: ElaborationItemType,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"],
  fileExcelName?: string
): Promise<ElaborationItem> => {
  const source = coObject.source as TgkTableObjectSource;

  const isExcel = source.objectSource === SourceTypeEnum.EXCEL;
  const insertAsImage = source.elabResultType === "IMAGE_STRINGBASE64";

  return {
    documentId: documentPart.id,
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    correlationId: correlationId,
    contentControlId: coObject.contentControlId.toString(),
    dbId: source.dbApplication?.id,
    formId: source.form.code,
    formDesc: isExcel && fileExcelName ? fileExcelName : source.form.desc,
    sheetCode: source.sheet.code,
    sheet:
      source.objectSource !== SourceTypeEnum.EXCEL || coObject.info?.objectType === "CHART"
        ? source.sheet.desc
        : getSheetForExcelTableObject(source),
    area: source.area?.name,
    chart: source.chart?.name,
    objectId: coObject.info.objectName,
    additionalInfo: coObject.info.info,
    objectLogicId: coObject.id,
    objectType: coObject.info.objectType,
    documentPartIncludedId: null,
    elaborationType: elaborationType,
    formatMetadata: coObject?.advanced?.formatMetadata,
    formatImage: insertAsImage ? source.formatImage : {},
    parameters: coObject.parameters as DimensionDetail[],
    inheritFormat: coObject.advanced.inheritFormat,
    inheritFormatImage: insertAsImage ? source.inheritFormatImage : null,
    language: documentPartMetadata.language,
    elabResultType: source.elabResultType,
    source: source.objectSource,
    tableUpdateStrategy: {
      typeUpdate: tableUpdateType,
      overrideData: coObject.info.overrideData,
      overrideStyle: coObject.info.overrideStyle,
      originalPayload: coObject.originalPayload,
    },
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info?.componentReference,
      documentPartMetadata: documentPartMetadata,
    }),
  };
};

const mapTgkAmountToElaborationItem = async (
  correlationId: string,
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  coObject: CoObjectItem,
  elaborationType: ElaborationItemType,
  fileExcelName?: string
): Promise<ElaborationItem> => {
  const source = coObject.source as TgkAmountObjectSource;
  let formatType = coObject.advanced.choice;
  let elabResultType = coObject.source?.elabResultType;
  let cell = null;
  let area = null;
  let sheet = null;
  let sheetCode = null;
  let formId = null;
  let formDesc = null;
  let parameters = null;
  let objectSource =
    source.objectSource === undefined || source.objectSource === "" ? SourceTypeEnum.TAGETIK : source.objectSource;

  const isExcel = objectSource === SourceTypeEnum.EXCEL;
  const isTagetikForm = objectSource === SourceTypeEnum.TAGETIK_FORM;

  if (isExcel || isTagetikForm) {
    formatType = FormatTypeEnum.NUMBER;
    formId = source.form?.code;
    formDesc = isExcel && fileExcelName ? fileExcelName : source.form?.desc;
    sheetCode = source.sheet.code;
    sheet = source.sheet.desc;
    area = source.area?.name;
    cell = source.cell;
    parameters = isTagetikForm ? coObject.parameters : null;
  }

  return {
    documentId: documentPart.id,
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    correlationId: correlationId,
    contentControlId: coObject.contentControlId.toString(),
    dbId: source.dbApplication?.id,
    dimensions: source.dimensions,
    formId: formId,
    formDesc: formDesc,
    area: area,
    sheetCode: sheetCode,
    sheet: sheet,
    cell: cell,
    objectId: coObject.info.objectName,
    additionalInfo: coObject.info.info,
    objectLogicId: coObject.id,
    objectType: source.analyticalWorkspace?.code ? "AMOUNT_AIH" : "AMOUNT",
    documentPartIncludedId: null,
    elaborationType: elaborationType,
    formatMetadata: coObject.advanced?.formatMetadata,
    inheritFormat: coObject.advanced?.inheritFormat,
    language: documentPartMetadata.language,
    formatType: formatType,
    elabResultType: elabResultType,
    source: objectSource,
    analyticalWorkspace: source.analyticalWorkspace?.code ? source.analyticalWorkspace : null,
    dataset: source.dataset?.code ? source.dataset : null,
    process: source.process,
    amount: source.amount,
    ...(parameters && { parameters }),
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info?.componentReference,
      documentPartMetadata: documentPartMetadata,
    }),
  };
};

const mapDimensionAttributeToElaborationItem = async (
  correlationId: string,
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  coObject: CoObjectItem,
  elaborationType: ElaborationItemType
): Promise<ElaborationItem> => {
  const source = coObject.source as TgkDimensionAttributeSource;
  return {
    documentId: documentPart.id,
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    correlationId: correlationId,
    contentControlId: coObject.contentControlId.toString(),
    dbId: source.dbApplication.id,
    dimensions: source.dimensions,
    formId: null,
    area: null,
    sheet: null,
    objectId: coObject.info.objectName,
    additionalInfo: coObject.info.info,
    objectLogicId: coObject.id,
    objectType: coObject.info.objectType,
    documentPartIncludedId: null,
    elaborationType: elaborationType,
    formatMetadata: coObject?.advanced?.formatMetadata,
    language: documentPartMetadata?.language,
    elabResultType: coObject.source?.elabResultType,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info?.componentReference,
      documentPartMetadata: documentPartMetadata,
    }),
  };
};

const mapTgkTextToElaborationItem = async (
  correlationId: string,
  documentPart: DocumentPart,
  documentPartMetadata: DocumentPartMetadata,
  coObject: CoObjectItem,
  elaborationType: ElaborationItemType
): Promise<ElaborationItem> => {
  const source = coObject.source as TgkTextSource;
  return {
    documentId: documentPart.id,
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    correlationId: correlationId,
    contentControlId: coObject.contentControlId.toString(),
    dbId: source.dbApplication.id,
    dimensions: source.dimensions ?? [],
    analyticalWorkspace: source.analyticalWorkspace,
    dataset: source.dataset,
    richText: source.richText,
    process: source.process,
    objectId: coObject.info.objectName,
    additionalInfo: coObject.info.info,
    objectLogicId: coObject.id,
    objectType: coObject.info.objectType,
    documentPartIncludedId: null,
    elaborationType: elaborationType,
    elabResultType: coObject.source?.elabResultType,
    ...(isComponentInstance(coObject) && {
      componentReference: coObject.info?.componentReference,
      documentPartMetadata: documentPartMetadata,
    }),
  };
};

const mapDocumentPartIncludeToElaborationItem = async (
  correlationId: string,
  documentPart: DocumentPart,
  coObject: CoObjectItem,
  elaborationType: ElaborationItemType,
  selectedUpdateOption?: UpdateOptions,
  tableUpdateType?: TableUpdateStrategy["typeUpdate"]
): Promise<ElaborationItem> => {
  const source = coObject.source as DocumentPartSource;
  return {
    documentId: documentPart.id,
    siteId: documentPart.siteId,
    driveItemId: documentPart.driveItemId,
    correlationId: correlationId,
    contentControlId: coObject.contentControlId.toString(),
    dbId: null,
    formId: null,
    area: null,
    sheet: null,
    objectId: coObject.info.objectName,
    objectLogicId: coObject.id,
    objectType: coObject.info.objectType,
    documentPartIncludedId: source.includedDocumentPartId,
    elaborationType: elaborationType,
    updateOptions: selectedUpdateOption,
    // TODO INTERNAL_REF_URL is ok for ROOT, needs to be evaluated for other type of headerAndFooter if use REF_URL
    elabResultType: "REF_URL",
    tableUpdateStrategy: {
      typeUpdate: tableUpdateType,
      overrideData: coObject.info.overrideData,
      overrideStyle: coObject.info.overrideStyle,
    },
  };
};

function getSheetForExcelTableObject(source: TgkTableObjectSource) {
  return checkArea(source);
}

function getSheetForExcelAmountObject(source: TgkAmountObjectSource) {
  return checkArea(source);
}

function checkArea(source: any) {
  if (source.area?.name?.includes("!")) {
    let newSheet = source.area?.name?.split("!")[0];
    if (source.sheet?.desc !== newSheet) {
      return newSheet;
    }
  }
  return source.sheet?.desc;
}
